/**
 * Victory point accounting.
 *
 * `scorePlayer` is the authority. `player.vp` is only ever a cache of it — the engine
 * refreshes the cache at each phase boundary because turn order and the Data Leak event
 * both need to know who is leading, and recomputing from scratch every frame would be
 * wasteful for the renderer.
 *
 * Region majorities are the exception to "score everything from current state": they are
 * awarded at rounds 4 and 8 and *banked*, so losing a region afterwards does not claw the
 * points back. Banked awards live in `bankedRegionVp`.
 */

import { ALL_SEATS, REGIONS, VP } from './constants';
import { charterMet } from './charters';
import { TECHS } from './tech';
import { effectiveHabitability, regionStanding, systemsOwnedBy } from './queries';
import type { CharterId, GameState, Player, PlayerId, Region } from './types';

/** A single line in a score breakdown, for the endgame table. */
export interface ScoreLine {
  label: string;
  detail: string;
  vp: number;
}

export interface ScoreBreakdown {
  seat: PlayerId;
  lines: ScoreLine[];
  total: number;
}

/**
 * Bank a region award.
 *
 * Awards live in `state.regionAwards`, not in the log. The log is a 600-entry ring buffer
 * that trims from the front, so storing scoring state there meant a long or chatty match
 * could quietly delete points that had already been granted.
 */
export function bankRegionAward(
  state: GameState,
  region: Region,
  seat: PlayerId,
  vp: number,
  placing: 'majority' | 'runnerUp',
): void {
  state.regionAwards.push({ round: state.round, region, seat, vp, placing });
  state.log.push({
    round: state.round,
    phase: state.phase,
    player: seat,
    text: `takes ${placing === 'majority' ? 'the majority' : 'runner-up'} in ` +
      `${REGIONS[region].name} for ${vp} VP.`,
  });
}

/** Sum of every region award banked for a seat so far. */
export function bankedRegionVp(state: GameState, seat: PlayerId): number {
  let total = 0;
  for (const award of state.regionAwards) {
    if (award.seat === seat) total += award.vp;
  }
  return total;
}

/** Human-readable banked region awards, for the endgame breakdown. */
export function bankedRegionDetail(state: GameState, seat: PlayerId): string {
  return state.regionAwards
    .filter((award) => award.seat === seat)
    .map((award) =>
      `${REGIONS[award.region].name} ${award.placing === 'majority' ? '1st' : '2nd'} +${award.vp}`)
    .join(', ');
}

/* --------------------------------------------------------------------- totals */

/** Total VP for a player from current state plus banked awards. */
export function scorePlayer(state: GameState, player: Player): number {
  let total = 0;

  for (const sys of systemsOwnedBy(state, player.id)) {
    if (sys.development === 'outpost') total += VP.outpost;
    else if (sys.development === 'colony') {
      total += effectiveHabitability(state, sys) >= 3 ? VP.colonyPrime : VP.colony;
    }
  }

  for (const id of player.techs) total += TECHS[id].vp;

  // Grand Theory pays out on the whole tech portfolio, itself included.
  if (player.techs.includes('scienceIII')) total += Math.floor(player.techs.length / 2);

  for (const id of player.charters) {
    if (charterMet(state, player, id)) total += chartersVpOf(id);
  }

  total += player.relics.length * VP.relic;
  if (player.megastructure) total += VP.megastructure;
  total += Math.floor(player.population / VP.populationPer);
  total += bankedRegionVp(state, player.id);

  return total;
}

function chartersVpOf(id: CharterId): number {
  // Imported lazily through the charters table to avoid a cyclic import at module init.
  // CHARTERS is a frozen literal so this is a plain lookup, not a computation.
  return charterVpTable[id];
}

/** Flattened charter VP, built once at module load. */
const charterVpTable: Record<CharterId, number> = {
  gasBaron: 4, greenThumb: 4, deepCore: 5, cartographer: 3, ironWorks: 4,
  archivist: 4, pathfinder: 3, terraformer: 5, admiral: 4, settler: 5,
  relicHunter: 4, population: 4,
};

/** Refresh every player's cached `vp`. Called at phase boundaries. */
export function refreshScores(state: GameState): void {
  for (const player of state.players) {
    player.vp = scorePlayer(state, player);
  }
}

/* ---------------------------------------------------------------- breakdown */

/** Itemised score, for the endgame table and the pause-menu scoreboard. */
export function scoreBreakdown(state: GameState, player: Player): ScoreBreakdown {
  const lines: ScoreLine[] = [];
  const owned = systemsOwnedBy(state, player.id);

  const outposts = owned.filter((s) => s.development === 'outpost');
  if (outposts.length > 0) {
    lines.push({
      label: 'Outposts',
      detail: `${outposts.length} × ${VP.outpost}`,
      vp: outposts.length * VP.outpost,
    });
  }

  const colonies = owned.filter((s) => s.development === 'colony');
  const prime = colonies.filter((s) => effectiveHabitability(state, s) >= 3);
  const plain = colonies.length - prime.length;
  if (colonies.length > 0) {
    const vp = plain * VP.colony + prime.length * VP.colonyPrime;
    const detail = prime.length > 0
      ? `${plain} × ${VP.colony} + ${prime.length} prime × ${VP.colonyPrime}`
      : `${plain} × ${VP.colony}`;
    lines.push({ label: 'Colonies', detail, vp });
  }

  if (player.techs.length > 0) {
    const techVp = player.techs.reduce((sum, id) => sum + TECHS[id].vp, 0);
    lines.push({
      label: 'Technology',
      detail: `${player.techs.length} techs`,
      vp: techVp,
    });
    if (player.techs.includes('scienceIII')) {
      lines.push({
        label: 'Grand Theory',
        detail: `⌊${player.techs.length} / 2⌋`,
        vp: Math.floor(player.techs.length / 2),
      });
    }
  }

  for (const id of player.charters) {
    const met = charterMet(state, player, id);
    lines.push({
      label: `Charter: ${id}`,
      detail: met ? 'fulfilled' : 'unfulfilled',
      vp: met ? charterVpTable[id] : 0,
    });
  }

  if (player.relics.length > 0) {
    lines.push({
      label: 'Relics',
      detail: `${player.relics.length} × ${VP.relic}`,
      vp: player.relics.length * VP.relic,
    });
  }

  if (player.megastructure) {
    lines.push({
      label: 'Megastructure',
      detail: player.megastructure,
      vp: VP.megastructure,
    });
  }

  const popVp = Math.floor(player.population / VP.populationPer);
  if (popVp > 0) {
    lines.push({
      label: 'Population',
      detail: `⌊${player.population} / ${VP.populationPer}⌋`,
      vp: popVp,
    });
  }

  const regionVp = bankedRegionVp(state, player.id);
  if (regionVp > 0) {
    lines.push({
      label: 'Region control',
      detail: bankedRegionDetail(state, player.id),
      vp: regionVp,
    });
  }

  const total = lines.reduce((sum, l) => sum + l.vp, 0);
  return { seat: player.id, lines, total };
}

/* --------------------------------------------------------------- region round */

/**
 * Award and bank region majorities. Idempotent per round: calling twice in the same
 * round is a no-op, because the engine records the round in `scoredRegionRounds`.
 */
export function scoreRegions(state: GameState): void {
  if (state.scoredRegionRounds.includes(state.round)) return;
  state.scoredRegionRounds.push(state.round);

  for (const region of ['core', 'midRim', 'outerRim'] as const) {
    const def = REGIONS[region];
    const standing = regionStanding(state, region);

    // A contested majority splits: everyone tied for first takes the majority award.
    // This keeps "deny the leader" a viable play instead of an all-or-nothing coin flip.
    for (const seat of standing.leaders) {
      bankRegionAward(state, region, seat, def.majorityVp, 'majority');
    }
    if (standing.leaders.length === 1 && def.runnerUpVp > 0) {
      for (const seat of standing.runnersUp) {
        bankRegionAward(state, region, seat, def.runnerUpVp, 'runnerUp');
      }
    }
  }
  refreshScores(state);
}

/* ------------------------------------------------------------------ endgame */

export interface FinalStanding {
  seat: PlayerId;
  vp: number;
  rank: number;
  breakdown: ScoreBreakdown;
}

/**
 * Final standings, sorted. Tiebreak chain per the design spec: VP → Data → Fuel → Ore →
 * seat order. Equal-in-everything seats share a rank.
 */
export function finalStandings(state: GameState): FinalStanding[] {
  refreshScores(state);

  const sorted = [...state.players].sort((a, b) => {
    if (b.vp !== a.vp) return b.vp - a.vp;
    if (b.resources.data !== a.resources.data) return b.resources.data - a.resources.data;
    if (b.resources.fuel !== a.resources.fuel) return b.resources.fuel - a.resources.fuel;
    if (b.resources.ore !== a.resources.ore) return b.resources.ore - a.resources.ore;
    return a.id - b.id;
  });

  const standings: FinalStanding[] = [];
  sorted.forEach((player, index) => {
    const previous = index > 0 ? sorted[index - 1]! : null;
    const tiedWithPrevious =
      previous !== null &&
      previous.vp === player.vp &&
      previous.resources.data === player.resources.data &&
      previous.resources.fuel === player.resources.fuel &&
      previous.resources.ore === player.resources.ore;

    standings.push({
      seat: player.id,
      vp: player.vp,
      rank: tiedWithPrevious ? standings[index - 1]!.rank : index + 1,
      breakdown: scoreBreakdown(state, player),
    });
  });
  return standings;
}

/** The winner, or `null` if the top rank is genuinely shared. */
export function determineWinner(state: GameState): PlayerId | null {
  const standings = finalStandings(state);
  const firsts = standings.filter((s) => s.rank === 1);
  return firsts.length === 1 ? firsts[0]!.seat : null;
}

/**
 * Turn order for the next round: ascending VP, so the trailing seat moves first.
 * Tiebreak by fewest holdings, then seat order — both deterministic.
 */
export function computeTurnOrder(state: GameState): PlayerId[] {
  refreshScores(state);
  return ALL_SEATS.slice().sort((a, b) => {
    const pa = state.players[a]!;
    const pb = state.players[b]!;
    if (pa.vp !== pb.vp) return pa.vp - pb.vp;
    const ha = systemsOwnedBy(state, a).length;
    const hb = systemsOwnedBy(state, b).length;
    if (ha !== hb) return ha - hb;
    return a - b;
  });
}
